import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import SourceIcon from './SourceIcon';
import BrandSourceIcon from './BrandSourceIcon';
import { toastSuccess, toastError } from '../toastHelper';

const LOW_CONV_THRESHOLD = 1.0;

const formatPct = (n) => `${(n || 0).toFixed(2)}%`;
const formatRev = (n) => {
  const value = Number(n) || 0;
  return `$${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};
const formatInt = (n) => (Number(n) || 0).toLocaleString();

const VsAvgPill = ({ delta, hasData, isDormant }) => {
  if (isDormant) {
    return <span className="mi-vs-pill mi-vs-pill--awaiting">Awaiting first visit</span>;
  }
  if (!hasData) {
    return <span className="mi-vs-pill mi-vs-pill--neutral">—</span>;
  }
  if (Math.abs(delta) < 0.5) {
    return <span className="mi-vs-pill mi-vs-pill--flat">FLAT</span>;
  }
  const sign = delta > 0 ? '+' : '';
  const tone = delta > 0 ? 'pos' : 'neg';
  return (
    <span className={`mi-vs-pill mi-vs-pill--${tone}`}>
      {sign}{Math.round(delta)}pp
    </span>
  );
};

VsAvgPill.propTypes = {
  delta: PropTypes.number,
  hasData: PropTypes.bool,
  isDormant: PropTypes.bool
};

const RowMenu = ({ trackableLinkId, shortUrl, canDelete, onEdit, onDelete, teamId, confirmId }) => {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (event) => {
      if (
        !buttonRef.current?.contains(event.target) &&
        !dropdownRef.current?.contains(event.target)
      ) setOpen(false);
    };
    // The menu is portaled to the body with fixed coords, so a scroll/resize
    // would leave it floating in the wrong place — just close it.
    const close = () => setOpen(false);
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  const toggle = () => {
    setOpen((prev) => {
      if (!prev && buttonRef.current) {
        const r = buttonRef.current.getBoundingClientRect();
        setCoords({ top: r.bottom + 4, right: window.innerWidth - r.right });
      }
      return !prev;
    });
  };

  const handleCopy = () => {
    setOpen(false);
    navigator.clipboard.writeText(shortUrl)
      .then(() => toastSuccess('Link copied to clipboard.'))
      .catch(() => toastError('Could not copy link.'));
  };
  const handleEdit = () => { setOpen(false); onEdit(trackableLinkId); };
  const handleDelete = () => { setOpen(false); onDelete(trackableLinkId); };

  return (
    <div className="mi-row-menu">
      <button
        ref={buttonRef}
        type="button"
        className="mi-sources-row__menu"
        aria-label="More actions"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={toggle}
      >
        <span aria-hidden="true">⋯</span>
      </button>
      {open && coords && createPortal(
        <div
          ref={dropdownRef}
          className="mi-row-menu__dropdown"
          role="menu"
          style={{ position: 'fixed', top: coords.top, right: coords.right, zIndex: 9999 }}
        >
          {teamId != null && confirmId != null && (
            <a
              role="menuitem"
              className="mi-row-menu__item"
              href={`/teams/${teamId}/confirms/${confirmId}/trackable_links/${trackableLinkId}/qr_code`}
              onClick={() => setOpen(false)}
            >
              QR Code
            </a>
          )}
          {shortUrl && (
            <button type="button" role="menuitem" className="mi-row-menu__item" onClick={handleCopy}>
              Copy link
            </button>
          )}
          <button type="button" role="menuitem" className="mi-row-menu__item" onClick={handleEdit}>
            Edit
          </button>
          <button
            type="button"
            role="menuitem"
            className="mi-row-menu__item mi-row-menu__item--danger"
            onClick={handleDelete}
            disabled={!canDelete}
            title={canDelete ? undefined : "Can't delete a link with attributed orders"}
          >
            Delete
          </button>
        </div>,
        (buttonRef.current && buttonRef.current.closest('.marketing-insights-root')) || document.body
      )}
    </div>
  );
};

RowMenu.propTypes = {
  trackableLinkId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  shortUrl: PropTypes.string,
  canDelete: PropTypes.bool.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  teamId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  confirmId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

const LINK_TYPE_TO_DISPLAY = {
  email:  'Email',
  social: 'Social',
  paid:   'Ad',
  qr:     'QR',
  other:  'Other'
};

const SourcesTable = ({
  rows, links, onCreateTrackableLink, onEditTrackableLink, onDeleteTrackableLink,
  teamId, confirmId,
  title = 'Sources & trackable links',
  subtitle,
  emptyText = 'No traffic recorded in this window.',
  id,
}) => {
  const baseRows = rows || [];
  const linksById = (links || []).reduce((acc, l) => { acc[l.id] = l; return acc; }, {});

  // source_performance only gains a row once a link has traffic, so a freshly
  // created link would be invisible until reload. Synthesize a zero-stats row
  // for any link not yet represented so it shows immediately.
  const representedLinkIds = new Set(
    baseRows.filter((r) => r.trackable_link_id != null).map((r) => r.trackable_link_id)
  );
  const pendingLinkRows = (links || [])
    .filter((l) => !representedLinkIds.has(l.id))
    .map((l) => ({
      utm_source: l.utm_source,
      utm_medium: l.utm_medium,
      utm_campaign: l.utm_campaign,
      trackable_link_id: l.id,
      display_name: l.name,
      type: (l.link_type && LINK_TYPE_TO_DISPLAY[l.link_type]) || 'Other',
      visits: 0, orders: 0, tickets: 0, revenue: 0, conversion_rate: 0, vs_avg_conversion_delta: 0
    }));
  const list = [...baseRows, ...pendingLinkRows];
  const count = list.length;
  const showMenuCol = Boolean(onEditTrackableLink && onDeleteTrackableLink);

  return (
    <div className="mi-sources-table" id={id}>
      <header className="mi-sources-table__header">
        <div>
          <h3>{title}</h3>
          <p className="mi-sources-table__subtitle">
            {subtitle || `${count} ${count === 1 ? 'source' : 'sources'} · sorted by revenue`}
          </p>
        </div>
        {onCreateTrackableLink && (
          <div className="mi-sources-table__toolbar">
            {teamId != null && confirmId != null && (
              <a
                className="mi-traffic-meta__create"
                href={`/teams/${teamId}/confirms/${confirmId}/event_qr_code`}
              >
                Event QR Code
              </a>
            )}
            <button type="button" className="mi-traffic-meta__create" onClick={onCreateTrackableLink}>
              <span aria-hidden="true" className="mi-traffic-meta__create-icon">＋</span>
              Create trackable link
            </button>
          </div>
        )}
      </header>

      {count === 0 ? (
        <p className="mi-sources-table__empty">{emptyText}</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Source</th>
              <th>Type</th>
              <th className="num">Visits</th>
              <th className="num">Orders</th>
              <th className="num">Tickets</th>
              <th className="num">Conv.</th>
              <th className="num">Revenue</th>
              <th className="num">vs. comp avg</th>
              {showMenuCol && <th className="mi-sources-table__menu-col" aria-label="Row menu" />}
            </tr>
          </thead>
          <tbody>
            {list.map((row, i) => {
              const liveLink = row.trackable_link_id != null ? linksById[row.trackable_link_id] : null;
              const displayName = liveLink?.name?.length ? liveLink.name : row.display_name;
              const type = (liveLink && liveLink.link_type && liveLink.link_type !== 'other'
                ? LINK_TYPE_TO_DISPLAY[liveLink.link_type]
                : null) || row.type || 'Other';
              const isLowConv = row.visits > 0 && row.conversion_rate < LOW_CONV_THRESHOLD;
              const hasDelta = row.visits > 0;
              const isDormant = row.visits === 0 && row.orders === 0;
              const isTrackableLink = row.trackable_link_id != null;
              const canDelete = isTrackableLink && (row.orders || 0) === 0;
              return (
                <tr
                  key={`${row.utm_source || 'direct'}-${row.utm_medium || ''}-${row.utm_campaign || ''}-${i}`}
                  className="mi-sources-row"
                  tabIndex={0}
                >
                  <td className="mi-sources-row__source">
                    <span className={`mi-sources-row__icon mi-sources-row__icon--${type.toLowerCase()}`}>
                      {row.source_key
                        ? <BrandSourceIcon siteKey={row.source_key} type={type} />
                        : <SourceIcon type={type} />}
                    </span>
                    {row.meta_ads_details_path && row.utm_campaign && displayName.includes(row.utm_campaign) ? (
                      <span className="mi-sources-row__name">
                        {displayName.slice(0, displayName.indexOf(row.utm_campaign))}
                        <a className="mi-sources-row__ad-link" href={row.meta_ads_details_path}>
                          {row.utm_campaign}
                        </a>
                        {displayName.slice(displayName.indexOf(row.utm_campaign) + row.utm_campaign.length)}
                      </span>
                    ) : (
                      <span className="mi-sources-row__name">{displayName}</span>
                    )}
                  </td>
                  <td>
                    <span className={`mi-type-badge mi-type-badge--${type.toLowerCase()}`}>
                      <span className="mi-type-badge__dot" aria-hidden="true" />
                      {type}
                    </span>
                  </td>
                  <td className="num">{formatInt(row.visits)}</td>
                  <td className="num">{formatInt(row.orders)}</td>
                  <td className="num">{formatInt(row.tickets)}</td>
                  <td className={`num${isLowConv ? ' mi-sources-row__conv--low' : ''}`}>
                    {formatPct(row.conversion_rate)}
                  </td>
                  <td className="num mi-sources-row__revenue">{formatRev(row.revenue)}</td>
                  <td className="num">
                    <VsAvgPill
                      delta={row.vs_avg_conversion_delta || 0}
                      hasData={hasDelta}
                      isDormant={isDormant}
                    />
                  </td>
                  {showMenuCol && (
                    <td className="mi-sources-table__menu-col">
                      {isTrackableLink && (
                        <RowMenu
                          trackableLinkId={row.trackable_link_id}
                          shortUrl={liveLink?.short_url}
                          canDelete={canDelete}
                          onEdit={onEditTrackableLink}
                          onDelete={onDeleteTrackableLink}
                          teamId={teamId}
                          confirmId={confirmId}
                        />
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

SourcesTable.propTypes = {
  rows: PropTypes.array,
  links: PropTypes.array,
  onCreateTrackableLink: PropTypes.func,
  onEditTrackableLink: PropTypes.func,
  onDeleteTrackableLink: PropTypes.func,
  teamId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  confirmId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  title: PropTypes.string,
  subtitle: PropTypes.string,
  emptyText: PropTypes.string,
  id: PropTypes.string
};

export default SourcesTable;
